import { formatInTimeZone } from 'date-fns-tz';
import pluralize from 'pluralize';

import { Inclusion } from '@shared/types/client/ProcuredOffer';
import ToggleContent from '@shared/v2/components/ToggleContent';
import { Airport } from '@shared/v2/types';

import Link from '@/components/Link';
import LinkButton from '@/components/LinkButton';
import Text from '@/components/Text';
import ViewDetailsModal from '@/components/ViewDetailsModal';
import { MEDIUM_DATE_FORMAT } from '@/lib/date/format';
import { TIMEZONE } from '@/v2/constants';
import { UI } from '@/v2/types';

interface ProcuredOfferFooterProps {
  propertyContent: UI.Property;
  procuredOffer?: UI.ProcuredOffer;
  nights: number;
  departingOrigin: string;
  origin: Airport;
  inclusions: Inclusion[];
}

const ProcuredOfferFooter = ({
  propertyContent,
  procuredOffer,
  nights,
  departingOrigin,
  origin,
  inclusions,
}: ProcuredOfferFooterProps) => {
  if (!procuredOffer) {
    return null;
  }

  return (
    <Text fontSize={['xxs', null, 'xs']} lineHeight="1">
      Price displayed is for {pluralize('night', nights, true)} in a{' '}
      {procuredOffer.rooms?.[0].room.name.replace(/ [Rr]oom$/, '')} room and
      return Jetstar Economy flights departing {departingOrigin ?? origin?.name}
      .{' '}
      <>
        For information on holidays package details and extras see{' '}
        <Link.Text
          as={'/extras'}
          href={'/extras'}
          external={false}
          shallow={false}
        >
          what&apos;s included
        </Link.Text>
        .{' '}
      </>
      Travel periods apply. Blackout dates &amp; surcharges may apply during
      certain periods. Offer ends:{' '}
      {formatInTimeZone(
        procuredOffer?.saleDateRange.end.utc,
        TIMEZONE,
        MEDIUM_DATE_FORMAT,
      )}
      .{' '}
      <ToggleContent
        toggle={(show) => (
          <LinkButton
            noPadding
            fontSize="sm"
            onClick={(e: Event) => {
              e.preventDefault();
              show();
            }}
          >
            <Text bold link fontSize={['xxs', null, 'xs']}>
              View offer terms
            </Text>
          </LinkButton>
        )}
        content={(close) => (
          <ViewDetailsModal
            close={close}
            title={`Holiday package to ${propertyContent.destination.title} at ${propertyContent.name}`}
            terms={procuredOffer?.terms ?? ''}
            inclusions={inclusions}
            routes={procuredOffer?.routes}
            destinationCode={
              propertyContent.destination.searchConfig?.destinationCode
            }
            travelDateRange={procuredOffer?.travelDateRange}
            minNights={nights}
          />
        )}
      />
    </Text>
  );
};

export default ProcuredOfferFooter;
